/**
 * Get/set localStorage data from remote origin
 *
 * Depends on a receiver at origin + path, which responds to messages from this origin.
 * (see mock_data/p13n_test.html for an example)
 * @module
 */
var _ = require('optimizely-client-fn');
var PromiseEnv = require('../lib/promise').Promise;

var actionTypes = require('../constants/action_types');
var di = require('../di');
var Flux = require('../flux');

var ApiEvents = require('../api_events');
var createError = require('./custom_error').create;
var DateEnv = require('../env/date');
var DocumentEnv = require('../env/document');
var JSONEnv = require('../lib/json');
var Poll = require('./poll');
var Visitor = require('../visitor');
var WindowEnv = require('../env/window');

var Logger = require('./logger');

var XDomainStore = di.get('stores/xdomain');

var XDomainStorageError = exports.Error = createError('XDomainStorageError');

/**
 *
 * @param {Event} event
 * @private
 */
function eventListener(event:any) {
  var message;
  if (!_.find(XDomainStore.getFrames(), {origin: event.origin})) {
    Logger.debug('XDomain', 'No frame found for origin: ' + event.origin);
    return;
  }
  try {
    message = JSONEnv.parse(event.data);
  } catch(e) {
    // malformed message is probably not one of ours
    Logger.debug('XDomain', 'Ignoring malformed message event:', event);
    return;
  }

  if (message.type === 'ERROR') {
    // disable iframe post messages
    //TODO: Potentially catch/reject specific error conditions in the future
    Flux.dispatch(actionTypes.XDOMAIN_SET_DISABLED, {disabled: true});
    ApiEvents.emitInternalError(new XDomainStorageError('Xdomain Error: ' + message.response));
  } else if (message.type === 'SYNC') {
    // TODO(tyler): SYNC messages aren't stored
    (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').time('xdomainStorageSync');
    _.each(XDomainStore.getSubscribers(), function(subscriber) {
      subscriber(message.response.key, message.response.value);
    });
    (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').timeEnd('xdomainStorageSync');
  } else {
    var storedMessage = XDomainStore.getMessageById(message.id);
    if (!storedMessage) {
      Logger.warn('XDomain', 'No stored message found for ID', message.id);

      if (!_.isNumber(message.id)) {
        ApiEvents.emitInternalError(new XDomainStorageError('Message ID is not a number: ' + message.id));
      } else {
        var maxMessageId = XDomainStore.getNextMessageId();
        if (message.id >= maxMessageId) {
          ApiEvents.emitInternalError(new XDomainStorageError('Message ID is greater than expected maximum ID (' + message.id + '>' + maxMessageId + ')'));
        } else if (message.id < 0) {
          ApiEvents.emitInternalError(new XDomainStorageError('Message ID is < 0: ' + message.id));
        } else {
          ApiEvents.emitInternalError(new XDomainStorageError('No stored message found for message ID: ' + message.id));
        }
      }

      return;
    }

    if (!storedMessage.resolver) {
      Logger.warn('XDomain', 'Message already resolved, ignoring:', message.id);
      return;
    }

    storedMessage.resolver(message.response);
    Flux.dispatch(actionTypes.XDOMAIN_SET_MESSAGE, {
      messageId: message.id,
      message: {
        // Keep only a bit of debugging info, not the unbounded value and closure resolver properties
        data: {
          id: message.id,
          type: storedMessage.data.type,
          key: storedMessage.data.key,
        },
        startTime: storedMessage.startTime,
        endTime: DateEnv.now(),
      },
    });
  }
}

/**
 * Add message to store, if ready, post message to registered target
 * @param {Object} messageData - payload of message
 * @param {XDomainFrame} [frame] - frame where to send message
 * @private
 */
function queueMessage(messageData:any, frame?:import('core/stores/xdomain').XDomainFrame) {
  if(!frame) {
    // use default frame if no frame given
    frame = XDomainStore.getDefaultFrame();
  }
  return new PromiseEnv(function(resolve) {
    var message = {
      data: _.extend({}, messageData, {
        id: XDomainStore.getNextMessageId(),
      }),
      resolver: resolve,
    };

    if (frame) {
      // If frame is loaded already - send message immediately
      if (!XDomainStore.isDisabled()) {
        sendMessage(message, frame);
      }
    } else {
      // queue message
      // TODO(tyler): deduplicate queued messages
      //Logger.debug('XDomain', 'Queuing message', message.data);
      Flux.dispatch(actionTypes.XDOMAIN_SET_MESSAGE, {
        messageId: message.data.id,
        message: message,
      });
    }
  });
}

/**
 * Post a message to target with given origin
 * @param {XDomainMessage} message
 * @param {XDomainFrame} frame
 * @private
 */
function sendMessage(message:import('core/stores/xdomain').XDomainMessage, frame:import('core/stores/xdomain').XDomainFrame) {
  var data = message.data;
  // record sent message
  Flux.dispatch(actionTypes.XDOMAIN_SET_MESSAGE, {
    messageId: message.data.id,
    message: _.extend({}, message, {
      startTime: DateEnv.now(),
    }),
  });
  // send message
  frame.target.postMessage(JSONEnv.stringify(data), frame.origin);
}

/**
 * Set key to value
 * @param {string} key
 * @param {string} value - value to set
 * @param {XDomainFrame} [frame]
 * @return {Promise} resolves when set succeeds
 */
exports.setItem = function(key:string, value:string, frame?:import('core/stores/xdomain').XDomainFrame): Promise<any> {
  return queueMessage({
    type: 'PUT',
    key: key,
    value: value,
  }, frame);
};

/**
 * Get an item by key name
 * @param key
 * @param {XDomainFrame} [frame]
 * @returns {Promise} resolves to the fetched value
 */
exports.getItem = function(key, frame?:import('core/stores/xdomain').XDomainFrame): Promise<any> {
  return queueMessage({
    type: 'GET',
    key: key,
  }, frame);
};

/**
 * Get all keys/values
 * @param {XDomainFrame} [frame]
 * @returns {Promise} resolves to an object mapping each key to its value
 */
exports.fetchAll = function(frame?:import('core/stores/xdomain').XDomainFrame): Promise<any> {
  return queueMessage({
    type: 'GETALL',
  }, frame);
};

/**
 * Delete items by visitorId
 * @param {string} visitorId
 * @param {XDomainFrame} [frame]
 * @returns {Promise} resolves when deletion succeeds
 */
exports.deleteData = function(visitorId:string, frame?:import('core/stores/xdomain').XDomainFrame): Promise<any> {
  return queueMessage({
    type: 'DELETE',
    visitorId: visitorId,
  }, frame);
};

/**
  * Register a listener for "SYNC" messages from the remote origin
  * @param {Function} callback
  */
exports.subscribe = function(callback:Function) {
  Flux.dispatch(actionTypes.XDOMAIN_ADD_SUBSCRIBER, {
    subscriber: callback,
  });
};

/**
 * Create and load storage iframe
 * @param {string} origin - remote host origin
 * @param {string} path - to storage HTML file on origin
 * @returns {Promise} resolves to iframe target window when iframe is loaded
 * @private
 */
exports.loadIframe = function loadIframe(origin:string, path:string): Promise<any> {
  return new PromiseEnv(function (resolve) {
    (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').time('xdomainIframeLoad');
    var iframe = DocumentEnv.createElement('iframe');
    iframe.src = origin + path;
    // Hide the frame from browsers (https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/hidden) (CJS-1848)
    iframe.hidden = true;

    iframe.setAttribute('tabindex', '-1');
    iframe.setAttribute('title', 'Optimizely Internal Frame');

    // IE10 doesn't support "hidden" attribute
    iframe.style.display = 'none';
    iframe.height = 0;
    iframe.width = 0;

    iframe.onload = function () {
      (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').timeEnd('xdomainIframeLoad');
      var frame = {
        id: XDomainStore.getNextFrameId(),
        target: iframe.contentWindow,
        origin: origin,
        path: path,
      };
      Flux.dispatch(actionTypes.XDOMAIN_ADD_FRAME, frame);
      resolve(frame);
    };

    DocumentEnv.appendTo(DocumentEnv.querySelector('body'), iframe);
  });
};

/**
 * localStorage items contain within their keys a userId with which that item's data is associated.
 *
 * @param {Object} storageKeys
 *        Key/value pairs from localStorage; possibly a mix of local and foreign items.
 * @param {string[]} canonicalOrigins
 *        Substring-match origin names, in descending priority order.
 * @returns {string|undefined}
 *        SOME user ID from the highest-priority canonical origin that was present among `storageKeys`.
 *        If none of the given canonical origins were among the `storageKeys`, undefined.
 */
exports.getXDomainUserId = function(storageKeys:any, canonicalOrigins:string[]): string|undefined {
  var userId;
  var allUserIds = {} as any;
  var keys = _.keys(storageKeys);

  _.each(canonicalOrigins, function(origin) {
    allUserIds[origin] = [];
    _.each(keys, function(key) {
      var currentUserId = Visitor.getUserIdFromKey(key, origin);
      // Keep the first userId found, since it's highest priority
      if (!userId && currentUserId) {
        userId = currentUserId;
      }
      // Store all the userIds
      if (currentUserId && !_.includes(allUserIds[origin], currentUserId)) {
        allUserIds[origin].push(currentUserId);
      }
    });
  });
  Logger.debug('XDomain: Found userIds:', allUserIds);

  return userId;
};

/**
 * Initialize XDomain storage, including sending queued messages.
 * @param {string} origin - origin where data is to be stored
 * @param {string} path - path on origin to storage HTML file
 * @returns {Promise} resolves when XDomainStorage is ready
 */
exports.load = function(origin:string, path:string): Promise<any> {
  WindowEnv.addEventListener('message', eventListener);

  var whenBodyReady = function() {
    return !!DocumentEnv.querySelector('body');
  };

  var loadIframe = function() {
    return exports.loadIframe(origin, path);
  };

  return Poll.pollFor(whenBodyReady).then(loadIframe).then(function(frame) {
    Flux.dispatch(actionTypes.XDOMAIN_SET_DEFAULT_FRAME, frame);

    //check if 3rd party is disabled by checking
    if (!XDomainStore.isDisabled()) {
      // send queued messages
      _.each(XDomainStore.getMessages(), function(message) {
        if (!message.startTime) {
          sendMessage(message, frame);
        }
      });
    }
  });
};

if (__TEST__) {
  exports.eventListener = eventListener;
}
